import React, { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import px from 'prop-types';
import cx from 'classnames';
import { useJsApiLoader, GoogleMap, Marker, MarkerClusterer } from '@react-google-maps/api';
import { Location } from 'Common/utils';
import { UserMessage } from 'Common/components/ui';
import ClusterPin from './ClusterPin';
import MapInfo from './MapInfo';
import isEqual from 'fast-deep-equal';

const libraries = ['places'];

const LocatorMap = forwardRef(function LocatorMap(
    {
        apiKey,
        mapState,
        searchState,
        onUpdate,
        onMapLoaded,
        onTilesRendered,
        onSearchLocation,
        allowResearch = false,
        enableClusters = false,
        ClusterPinComponent = ClusterPin,
        MapInfoComponent = MapInfo,
        mapOptions,
        gestureHandling = 'cooperative',
        google,
        width = '100%',
        height = '100%',
        className,
        children,
    },
    ctrlRef
) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const googleRef = useRef(google);
    const googleMapRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [showResearch, setShowResearch] = useState(-1);
    const lastLocation = useRef(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries,
        region: mapState.region,
    });

    const onMapLoad = useCallback(
        (mapInstance) => {
            mapRef.current = mapInstance;
            setLoaded(true);
            if (onMapLoaded) onMapLoaded();
        },
        [onMapLoaded]
    );

    const onBoundsChange = useCallback(() => {
        if (!mapRef.current || !loaded) return;
        const bounds = mapRef.current.getBounds();
        const ne = bounds.getNorthEast().toJSON();
        const sw = bounds.getSouthWest().toJSON();
        const center = mapRef.current.getCenter().toJSON();

        if (allowResearch) {
            const dist = searchState.location
                ? Location.getDistanceBetweenCoords(center, searchState.location)
                : Infinity;

            setShowResearch(dist > 2 ? Date.now() : -1);
        }

        onUpdate({ bounds: { ne, sw }, center, zoom: mapRef.current.getZoom() });
    }, [loaded, allowResearch, onUpdate, searchState.location]);

    const getCurrentArea = React.useCallback(
        (locs = [], zoomLevel = googleMapRef.current?.props?.zoom ?? mapState.zoom) => {
            if (!googleRef.current) {
                googleRef.current = google;
                return mapState;
            }
            const bounds = new googleRef.current.maps.LatLngBounds();

            locs.forEach((loc) => {
                bounds.extend(new googleRef.current.maps.LatLng(loc.lat, loc.lng));
            });

            const center = { lat: locs[0].lat, lng: locs[0].lng };
            const zoom = zoomLevel;
            const size = {
                width: containerRef.current.offsetWidth,
                height: containerRef.current.offsetHeight,
            };

            const newBounds = {
                ne: {
                    lat: bounds.getNorthEast().lat(),
                    lng: bounds.getNorthEast().lng(),
                },
                nw: {
                    lat: bounds.getNorthEast().lat(),
                    lng: bounds.getSouthWest().lng(),
                },
                se: {
                    lat: bounds.getSouthWest().lat(),
                    lng: bounds.getNorthEast().lng(),
                },
                sw: {
                    lat: bounds.getSouthWest().lat(),
                    lng: bounds.getSouthWest().lng(),
                },
            };

            return { center, zoom, size, bounds: newBounds };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mapState]
    );

    const changeLocation = React.useCallback(
        async (coordsOrPlace) => {
            setShowResearch(-1);
            if (onSearchLocation) onSearchLocation(coordsOrPlace);
        },
        [onSearchLocation]
    );

    const onResearch = React.useCallback(() => {
        if (allowResearch && showResearch > 0 && mapState.center) {
            changeLocation(mapState.center);
        }
    }, [allowResearch, showResearch, changeLocation, mapState.center]);

    useEffect(() => {
        ctrlRef.current = {
            moveToLocation(loc, zoomLevel) {
                onUpdate(getCurrentArea([loc], zoomLevel));
            },
        };

        return () => {
            if (Object.hasOwnProperty.call(ctrlRef, 'current')) {
                ctrlRef.current = undefined;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onUpdate, getCurrentArea]);

    useEffect(() => {
        if (
            !lastLocation.current ||
            !searchState.location ||
            Location.getDistanceBetweenCoords(lastLocation.current, searchState.location) > 0
        ) {
            lastLocation.current = searchState.location;
            if (searchState.location) {
                onUpdate({ center: searchState.location, zoom: 12 });
                setShowResearch(-1);
            } else {
                const currentArea = getCurrentArea(searchState.response.Locations);

                if (!isEqual(currentArea, mapState)) {
                    onUpdate(currentArea);
                }
            }
        }
    }, [searchState.response.Locations, searchState.location, mapState, onUpdate, getCurrentArea]);

    if (loadError) return <div>Error loading Google Maps</div>;
    if (!isLoaded) return <div>Loading map…</div>;

    return (
        <div ref={containerRef} className={cx('LocatorMap', className)} style={{ width, height, position: 'relative' }}>
            {allowResearch && showResearch > 0 && (
                <UserMessage
                    className="ResearchBar"
                    timestamp={showResearch}
                    timeout={0}
                    message="Dealer.Locater.SearchThisArea.Label"
                    onClick={onResearch}
                    type="success"
                >
                    <i className="fas fa-sync-alt me-2" />
                </UserMessage>
            )}

            {children}

            <GoogleMap
                ref={googleMapRef}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={mapState.center}
                zoom={mapState.zoom}
                options={{ gestureHandling, ...mapOptions }}
                onLoad={onMapLoad}
                onTilesLoaded={onTilesRendered}
                onDragEnd={onBoundsChange}
                onZoomChanged={onBoundsChange}
            >
                {enableClusters ? (
                    <MarkerClusterer>
                        {(clusterer) =>
                            searchState.response.Locations.map((loc) =>
                                loc.cluster_id ? (
                                    <Marker
                                        key={`cluster-${loc.cluster_id}-${loc.id}`}
                                        position={{ lat: loc.lat, lng: loc.lng }}
                                        clusterer={clusterer}
                                        onClick={() => ctrlRef.current.moveToLocation(loc, loc.getZoom(loc.cluster_id))}
                                        icon={ClusterPinComponent}
                                    />
                                ) : (
                                    <Marker
                                        key={`pin-${loc.id}`}
                                        position={{ lat: loc.lat, lng: loc.lng }}
                                        onClick={() => onUpdate({ openLocation: loc.id })}
                                        clickable
                                        opacity={
                                            mapState.openLocation ? (mapState.openLocation === loc.id ? 1 : 0.5) : 1
                                        }
                                    >
                                        {mapState.openLocation === loc.id ? (
                                            <div className="relative">
                                                <MapInfoComponent
                                                    show
                                                    location={loc}
                                                    onClose={() => onUpdate({ openLocation: null })}
                                                />
                                            </div>
                                        ) : null}
                                    </Marker>
                                )
                            )
                        }
                    </MarkerClusterer>
                ) : (
                    searchState.response.Locations.map((loc) => (
                        <Marker
                            key={`pin-${loc.id}`}
                            position={{ lat: loc.lat, lng: loc.lng }}
                            onClick={() => onUpdate({ openLocation: loc.id })}
                            clickable
                        >
                            {mapState.openLocation === loc.id ? (
                                <div className="relative">
                                    <MapInfoComponent
                                        show
                                        location={loc}
                                        onClose={() => onUpdate({ openLocation: null })}
                                    />
                                </div>
                            ) : null}
                        </Marker>
                    ))
                )}

                {searchState.location && (
                    <Marker
                        position={searchState.location}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: 'green',
                            fillOpacity: 1,
                            strokeWeight: 0,
                        }}
                    />
                )}
            </GoogleMap>
        </div>
    );
});

const Coord = px.shape({
    lat: px.number.isRequired,
    lng: px.number.isRequired,
});

const Bounds = px.shape({
    ne: Coord.isRequired,
    sw: Coord.isRequired,
});

const Loc = px.shape({
    id: px.oneOfType([px.string, px.number]).isRequired,
    lat: px.number.isRequired,
    lng: px.number.isRequired,
    cluster_id: px.oneOfType([px.string, px.number]),
    getZoom: px.func,
});

LocatorMap.propTypes = {
    apiKey: px.string.isRequired,
    google: px.objectOf(px.any),
    mapState: px.shape({
        center: Coord.isRequired,
        zoom: px.number.isRequired,
        bounds: Bounds,
        region: px.string,
        openLocation: px.oneOfType([px.string, px.number]),
    }).isRequired,
    searchState: px.shape({
        location: Coord,
        response: px.shape({
            Locations: px.arrayOf(Loc).isRequired,
        }).isRequired,
    }).isRequired,
    onUpdate: px.func.isRequired,
    onMapLoaded: px.func,
    onSearchLocation: px.func,
    onTilesRendered: px.func,
    allowResearch: px.bool,
    enableClusters: px.bool,
    ClusterPinComponent: px.elementType,
    MapInfoComponent: px.elementType,
    mapOptions: px.object,
    gestureHandling: px.string,
    width: px.oneOfType([px.string, px.number]),
    height: px.oneOfType([px.string, px.number]),
    className: px.string,
    children: px.node,
};

export default LocatorMap;
